import { z } from "zod/v3";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";

import type { AuthStrategy } from "@workspace/auth-context";
import { AUTH_STORAGE_KEY } from "@workspace/auth-context";
import { clearImpersonation } from "@workspace/shared/hooks";
import { removeCookie } from "@workspace/shared/utils/storage";
import {
	TENANT_COOKIE_KEY,
	TENANT_STORAGE_KEY,
} from "@workspace/tenant-context";
import { endEposStaffSession } from "@workspace/venue-management-staff-terminal/epos";

import { authConfig } from "@/lib/auth-config";

const resetSearchSchema = zodValidator(
	z.object({
		redirect: z.string().optional().catch("/login"),
	}),
);

export const Route = createFileRoute("/logout")({
	validateSearch: resetSearchSchema,
	beforeLoad: async ({ search }) => {
		// HA-831: end the EPOS staff session first, while there is still a session to end it with.
		await endEposStaffSession();

		// Clear impersonation tokens from sessionStorage
		clearImpersonation();

		// Clear all auth-related data from localStorage
		localStorage.removeItem(AUTH_STORAGE_KEY);

		// Clear tenant data from localStorage
		localStorage.removeItem(TENANT_STORAGE_KEY);

		// Clear tenant cookie
		removeCookie(TENANT_COOKIE_KEY);

		// Clear any other potential auth/session cookies
		removeCookie("session");
		removeCookie("auth");
		removeCookie("token");

		// Clear all localStorage items that start with 'app.'
		const keysToRemove = [];
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (key && key.startsWith("app.")) {
				keysToRemove.push(key);
			}
		}
		keysToRemove.forEach((key) => localStorage.removeItem(key));

		const availableStrategies: AuthStrategy[] =
			authConfig.enabledStrategies.map((type: string) => {
				const strategy = authConfig.strategies.find((s) => s.type === type);
				return strategy!;
			});

		const keycloakStrategy = availableStrategies.find(
			(s) => s.type === "keycloak",
		);

		if (keycloakStrategy?.logout) {
			// Call logout on Keycloak strategy if available
			await keycloakStrategy.logout();
		}

		throw redirect({
			to: search.redirect || "/login",
		});
	},
});
